import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Pagination,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  LocationOn,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  AttachMoney as AttachMoneyIcon,
  Videocam,
  Chat as ChatIcon,
  Home,
  Remove,
  Close,
  PersonAdd,
} from "@mui/icons-material";
import { useFormatter } from "../../utils/useFormatter";
import { Schedule, Slot } from "fhir/r5";
import { SetStateAction, Dispatch, useState } from "react";
import { AvailableSlotDialog } from "../../pages/schedulemanager/components/AvailableSlotDialog";
import { CreateAppointmentDialog } from "../../pages/schedulemanager/components/creatappointmentdialog";
import dayjs from "dayjs";
import { CircularLoading } from "../common";
import { useLocationContext } from "@/contexts/LocationContext";

/** Tipos de atendimento reconhecidos */
type AttendanceKind = "presencial" | "teleatendimento" | "chat" | "domicilio";

/**
 * Detecta o tipo de atendimento a partir do recurso Location.
 * Usa attendance-channel (novo) com fallback para virtual-service (legado).
 */
function getAttendanceKind(locationResource: any): AttendanceKind {
  const channel = locationResource?.extension?.find((ext: any) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;

  const isVirtual =
    locationResource?.extension?.find((ext: any) =>
      ext.url?.includes("virtual-service"),
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (isVirtual) return "teleatendimento";
  return "presencial";
}

/** Ícone e cores por tipo — tokens do Theme.tsx */
const ATTENDANCE_KIND_CONFIG: Record<
  AttendanceKind,
  { icon: React.ReactNode; colorKey: string; bgKey: string; label: string }
> = {
  presencial: {
    icon: <LocationOn sx={{ fontSize: 14 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
    label: "Presencial",
  },
  teleatendimento: {
    icon: <Videocam sx={{ fontSize: 14 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
    label: "Teleatendimento",
  },
  chat: {
    icon: <ChatIcon sx={{ fontSize: 14 }} />,
    colorKey: "error700.main",
    bgKey: "error100.main",
    label: "Chat",
  },
  domicilio: {
    icon: <Home sx={{ fontSize: 14 }} />,
    colorKey: "warning700.main",
    bgKey: "warning100.main",
    label: "Atendimento a Domicílio",
  },
};

interface AvailableSlotsViewerProps {
  bundle?: fhir5.Bundle<fhir5.Slot>;
  schedule?: Schedule[];
  loading?: boolean;
  setRefetch?: any;
}

export function AvailableSlotsViewer({
  bundle,
  schedule,
  loading,
  setRefetch,
}: AvailableSlotsViewerProps) {
  const [availableDialogOpen, setAvailableDialogOpen] =
    useState<boolean>(false);
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 8; // Número de itens por página
  const { formatDateTimeToHour, formatCurrency } = useFormatter;

  // Recursos completos de Location (com extensões) para detectar o tipo de atendimento
  const { location: locationContext } = useLocationContext();

  // Função para mudar de página
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  // Cálculo dos itens que serão exibidos com base na página
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = bundle?.entry?.slice(startIndex, endIndex);

  if (loading) {
    return (
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        p={3}
      >
        <CircularLoading />
      </Stack>
    );
  }

  if (!bundle?.entry?.length) {
    return (
      <Stack alignItems="center" justifyContent="center" p={3}>
        <Typography>Nenhum horário disponível para esta data.</Typography>
      </Stack>
    );
  }

  // Extrai a duração do slot das extensões do schedule

  const statusTranslate: { [key: string]: string } = {
    free: "Disponível",
    "busy-unavailable": "Indisponível",
  };

  return (
    <>
      <Grid container spacing={2} width="100%">
        {paginatedData?.map((entry, index) => {
          const slot = entry.resource as fhir5.Slot;
          const auxSchedule = schedule?.find(
            (s) => s.id === slot.schedule?.reference?.split("/")[1]
          );

          const slotDuration =
            auxSchedule?.extension?.find(
              (ext) =>
                ext.url ===
                "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-duration"
            )?.valueDuration?.value || 60;

          const serviceCost = auxSchedule?.extension?.find(
            (ext) =>
              ext.url ===
              "https://fluxmed.com.br/fhir/StructureDefinition/service-cost"
          )?.valueDecimal;

          const location = auxSchedule?.actor?.find((actor) =>
            actor.reference?.startsWith("Location/")
          )?.display;

          const practitioner = auxSchedule?.actor?.find((actor) =>
            actor.reference?.startsWith("Practitioner/")
          )?.display;

          const service = auxSchedule?.actor?.find((actor) =>
            actor.reference?.startsWith("HealthcareService/")
          )?.display;

          // Detecta o tipo de atendimento pelo recurso completo da Location
          const locationId = auxSchedule?.actor
            ?.find((actor) => actor.reference?.startsWith("Location/"))
            ?.reference?.split("/")[1];
          const locationEntry = locationContext?.find(
            (loc: any) => loc?.resource?.id === locationId,
          );
          const attendanceKind = getAttendanceKind(locationEntry?.resource);
          const attendanceConfig = ATTENDANCE_KIND_CONFIG[attendanceKind];

          return (
            <Grid
              item
              xs={12}
              md={6}
              key={slot.id}
              sx={{
                ...(index % 2 === 0 && {
                  paddingLeft: "0px !important",
                }),
                "& .MuiListItem-root": {
                  listStyle: "none",
                  "&::before": {
                    display: "none",
                  },
                  "&::marker": {
                    display: "none",
                  },
                },
                listStyle: "none",
                "&::before": {
                  display: "none",
                },
                "&::marker": {
                  display: "none",
                },
              }}
            >
              <ListItem
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 1,
                  cursor: "pointer",
                  height: "100%",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  marker: "none",
                  "::marker": {
                    display: "none",
                  },
                }}
                onClick={() => {
                  const date = dayjs(slot.start).format("DD/MM/YYYY");
                  const hour = formatDateTimeToHour(slot.start);
                  const available = slot.status && statusTranslate[slot.status];
                  const slotTime = {
                    start: slot.start,
                    end: slot.end,
                  };
                  const schedulePractitioner = auxSchedule?.actor?.find(
                    (actor) => actor.reference?.startsWith("Practitioner/")
                  );
                  const scheduleActors = auxSchedule?.actor;
                  const serviceCost = auxSchedule?.extension?.find(
                    (ext) =>
                      ext.url ===
                      "https://fluxmed.com.br/fhir/StructureDefinition/service-cost"
                  )?.valueDecimal;

                  setSelectedRow({
                    date,
                    hour,
                    available,
                    auxUpdateSlot: slot,
                    scheduleActors,
                    schedulePractitioner,
                    slotTime,
                    serviceCost,
                    setTabRefetch: setRefetch,
                  });
                  setCreateAppointmentDialogOpen(true);
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          color="primary.main"
                          sx={{ fontWeight: 600 }}
                        >
                          {formatDateTimeToHour(slot.start)}
                        </Typography>
                        {serviceCost && (
                          <Chip
                            size="small"
                            label={formatCurrency(serviceCost * 100)}
                            color="primary"
                            variant="outlined"
                            sx={{ minWidth: 65 }}
                          />
                        )}

                        <Chip
                          size="small"
                          label={`${slotDuration}min`}
                          color="primary"
                          variant="outlined"
                          sx={{ minWidth: 65 }}
                        />
                      </Stack>

                      <Stack spacing={1} alignItems="flex-end">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tooltip title="Marcar consulta">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const date = dayjs(slot.start).format(
                                  "DD/MM/YYYY"
                                );
                                const hour = formatDateTimeToHour(slot.start);
                                const available =
                                  slot.status && statusTranslate[slot.status];
                                const slotTime = {
                                  start: slot.start,
                                  end: slot.end,
                                };
                                const schedulePractitioner =
                                  auxSchedule?.actor?.find((actor) =>
                                    actor.reference?.startsWith("Practitioner/")
                                  );
                                const scheduleActors = auxSchedule?.actor;
                                const serviceCost =
                                  auxSchedule?.extension?.find(
                                    (ext) =>
                                      ext.url ===
                                      "https://fluxmed.com.br/fhir/StructureDefinition/service-cost"
                                  )?.valueDecimal;

                                setSelectedRow({
                                  date,
                                  hour,
                                  available,
                                  auxUpdateSlot: slot,
                                  scheduleActors,
                                  schedulePractitioner,
                                  slotTime,
                                  serviceCost,
                                });
                                setCreateAppointmentDialogOpen(true);
                              }}
                              sx={{ color: "primary.main" }}
                            >
                              <PersonAdd fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Remover disponibilidade">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const date = dayjs(slot.start).format(
                                  "DD/MM/YYYY"
                                );
                                const hour = formatDateTimeToHour(slot.start);
                                const available =
                                  slot.status && statusTranslate[slot.status];
                                const slotTime = {
                                  start: slot.start,
                                  end: slot.end,
                                };
                                const schedulePractitioner =
                                  auxSchedule?.actor?.find((actor) =>
                                    actor.reference?.startsWith("Practitioner/")
                                  );
                                const scheduleActors = auxSchedule?.actor;

                                setSelectedRow({
                                  date,
                                  hour,
                                  available,
                                  auxUpdateSlot: slot,
                                  scheduleActors,
                                  schedulePractitioner,
                                  slotTime,
                                });
                                setAvailableDialogOpen(true);
                              }}
                              sx={{ color: "error.main" }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PersonIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                        </Box>
                        <Typography variant="body1">
                          {practitioner || "Profissional não informado"}
                        </Typography>
                        <Chip
                          size="small"
                          label={service || "Serviço não informado"}
                          sx={{ height: 20 }}
                        />
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mt={0.5}
                      >
                        <Tooltip title={attendanceConfig.label}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: attendanceConfig.bgKey,
                              color: attendanceConfig.colorKey,
                              borderRadius: "50%",
                              flexShrink: 0,
                            }}
                          >
                            {attendanceConfig.icon}
                          </Box>
                        </Tooltip>

                        <Typography variant="caption" color="text.secondary">
                          {location || "Local não informado"}
                        </Typography>
                      </Stack>
                    </Box>
                  }
                />
              </ListItem>
            </Grid>
          );
        })}
      </Grid>

      {/* Paginação */}
      {bundle?.entry?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil((bundle?.entry?.length || 0) / itemsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
          />
        </Box>
      )}

      {createAppointmentDialogOpen && selectedRow && (
        <CreateAppointmentDialog
          setRefetchDashboard={setRefetch}
          isOpen={createAppointmentDialogOpen}
          setModalOpen={setCreateAppointmentDialogOpen}
          row={selectedRow}
          walkin={!selectedRow}
          cpfT={""}
        />
      )}

      {availableDialogOpen && selectedRow && (
        <AvailableSlotDialog
          setModalOpen={setAvailableDialogOpen}
          open={availableDialogOpen}
          row={selectedRow}
        />
      )}
    </>
  );
}
